/**
 * React Query hooks for project document generation and chat.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  getProjectContent,
  getProjectGeneratedDocuments,
  deleteProjectGeneratedDocument,
  projectChat,
  generateProjectDocument,
  getProjectShapeRecipes,
  type ProjectContentResponse,
  type ProjectChatResponse,
  type ProjectGenerateResponse,
  type ProjectGeneratedDocumentsResponse,
  type ShapeRecipesResponse,
  type ChatMessage,
} from "../services/naraDoor";
import { getCurrentWorkspaceId, requireCurrentWorkspaceId } from "../lib/workspaceStore";

export function useProjectContent(projectId: string | undefined) {
  const workspaceId = getCurrentWorkspaceId();

  return useQuery<ProjectContentResponse>({
    queryKey: ["projectContent", projectId, workspaceId],
    queryFn: () => getProjectContent(workspaceId!, projectId!),
    enabled: !!projectId && !!workspaceId,
  });
}

export function useProjectTemplates(projectId: string | undefined) {
  const workspaceId = getCurrentWorkspaceId();

  return useQuery<ShapeRecipesResponse>({
    queryKey: ["projectShapeRecipes", projectId, workspaceId],
    queryFn: () => getProjectShapeRecipes(workspaceId!, projectId!),
    enabled: !!projectId && !!workspaceId,
  });
}

export function useProjectChat(projectId: string | undefined) {
  return useMutation<ProjectChatResponse, Error, { message: string; history: ChatMessage[]; sessionId?: string; contextSessionIds?: string[] }>({
    mutationFn: (data) => {
      if (!projectId) throw new Error("projectId is required");
      return projectChat(requireCurrentWorkspaceId(), {
        projectId,
        message: data.message,
        history: data.history,
        sessionId: data.sessionId,
        contextSessionIds: data.contextSessionIds,
      });
    },
  });
}

export function useWorkspaceChat() {
  return useMutation<ProjectChatResponse, Error, { message: string; history: ChatMessage[]; sessionId?: string; contextSessionIds?: string[] }>({
    mutationFn: (data) => {
      return projectChat(requireCurrentWorkspaceId(), {
        message: data.message,
        history: data.history,
        scope: "workspace",
        sessionId: data.sessionId,
        contextSessionIds: data.contextSessionIds,
      });
    },
  });
}

export function useProjectGeneratedDocuments(projectId?: string) {
  const workspaceId = getCurrentWorkspaceId();
  return useQuery<ProjectGeneratedDocumentsResponse>({
    queryKey: ["projectGeneratedDocuments", projectId, workspaceId],
    queryFn: () => getProjectGeneratedDocuments(workspaceId!, projectId!),
    enabled: !!projectId && !!workspaceId,
    refetchInterval: (query) => {
      const docs = query.state.data?.documents ?? [];
      const hasActive = docs.some(
        (d) => d.status && !["completed", "failed"].includes(d.status)
      );
      return hasActive ? 3000 : false;
    },
  });
}

export function useDeleteProjectGeneratedDocument(projectId?: string) {
  const queryClient = useQueryClient();
  const workspaceId = getCurrentWorkspaceId();

  return useMutation<void, Error, { documentId: string }>({
    mutationFn: (data) => {
      if (!projectId) throw new Error("projectId is required");
      return deleteProjectGeneratedDocument(requireCurrentWorkspaceId(), projectId, data.documentId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["projectGeneratedDocuments", projectId, workspaceId],
      });
    },
  });
}

export function useGenerateProjectDocument() {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();

  return useMutation<ProjectGenerateResponse, Error, { projectId: string; templateId: string }>({
    mutationFn: (data) => generateProjectDocument(requireCurrentWorkspaceId(), { ...data, locale: i18n.language }),
    retry: false,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["projectGeneratedDocuments", variables.projectId],
      });
    },
  });
}
